import { lazy, Suspense } from "react";
import { Redirect, Route, Switch } from "react-router-dom";

import { Loader } from "@transfr-inc/dashboard-components";

import {
  OutcomesClassroomDetailRouteConfig,
  OutcomesLearnerDetailRouteConfig,
  OutcomesMultiOrgRouteConfig,
  OutcomesOrgDetailRouteConfig,
} from "@lib/routes.config";
import { hasRole, Role } from "@models/role";
import { useCurrentUser } from "@queries/account";
import { useRole } from "@queries/account-selectors";
import { useClassrooms } from "@queries/organization";

import { MetricRegistryGate } from "./components/metric-registry.gate";

const OutcomesMultiOrgView = lazy(() =>
  import("./multi-org/outcomes-multi-org.view")
);
const OutcomesOrgDetailView = lazy(() =>
  import("./org-detail/outcomes-org-detail.view")
);
const OutcomesClassroomDetail = lazy(() =>
  import("./classroom-detail/outcomes-classroom-detail.view")
);
const OutcomesLearnerDetail = lazy(() =>
  import("./learner-detail/outcomes-learner-detail.view")
);

/**
 * Resolves the role-aware landing target for `/dashboard`:
 * - Multi-org roles render the multi-org view in place.
 * - Org admins land on their own org's detail view.
 * - Instructors with exactly one classroom land on that classroom; with more
 *   than one (or none yet resolved) they land on their org's detail view,
 *   which scopes itself to their assigned classrooms.
 *
 * Roles outside the outcome-reporting set are gated out before this mounts, so
 * the unmatched case renders nothing rather than redirecting (a redirect here
 * would target `/dashboard`, this same landing, and loop).
 */
function OutcomesDashboardLanding({ basePath }) {
  const role = useRole();
  const currentUser = useCurrentUser();
  const { organizationCode, userId } = currentUser || {};

  const isInstructor = role === Role.INSTRUCTOR.id;

  const { data: classrooms = [], isFetching } = useClassrooms(
    { userId, role, orgCode: organizationCode },
    { enabled: isInstructor && !!userId && !!organizationCode }
  );

  if (hasRole(OutcomesMultiOrgRouteConfig.roles, role)) {
    return <OutcomesMultiOrgView />;
  }

  if (role === Role.ORG_ADMIN.id) {
    return <Redirect to={`${basePath}/dashboard/org/${organizationCode}`} />;
  }

  if (isInstructor) {
    if (isFetching) {
      return <Loader fixed />;
    }

    if (classrooms.length === 1) {
      return (
        <Redirect
          to={`${basePath}/dashboard/org/${organizationCode}/classroom/${classrooms[0].id}`}
        />
      );
    }

    return <Redirect to={`${basePath}/dashboard/org/${organizationCode}`} />;
  }

  return null;
}

/**
 * Outcome reporting routes, mounted inside `AppLayout` when the
 * `outcome-reporting.enabled` flag is on. Each route only renders for the
 * roles declared in its config.
 */
export function OutcomesRoutes({ basePath }) {
  const role = useRole();

  return (
    <MetricRegistryGate>
      <Suspense fallback={<Loader fixed />}>
        <Switch>
          {hasRole(OutcomesLearnerDetailRouteConfig.roles, role) && (
            <Route
              exact
              path={`${basePath}${OutcomesLearnerDetailRouteConfig.path}`}
            >
              <OutcomesLearnerDetail />
            </Route>
          )}
          {hasRole(OutcomesClassroomDetailRouteConfig.roles, role) && (
            <Route
              exact
              path={`${basePath}${OutcomesClassroomDetailRouteConfig.path}`}
            >
              <OutcomesClassroomDetail />
            </Route>
          )}
          {hasRole(OutcomesOrgDetailRouteConfig.roles, role) && (
            <Route
              exact
              path={`${basePath}${OutcomesOrgDetailRouteConfig.path}`}
            >
              <OutcomesOrgDetailView />
            </Route>
          )}
          <Route exact path={`${basePath}${OutcomesMultiOrgRouteConfig.path}`}>
            <OutcomesDashboardLanding basePath={basePath} />
          </Route>
        </Switch>
      </Suspense>
    </MetricRegistryGate>
  );
}
